import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Icon, Paper, MenuList, MenuItem, Typography, Popper, ClickAwayListener } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InboxIcon from '@mui/icons-material/Inbox';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import M from 'messages';
import winesPageStyles from '../styles';
import classes from './styles';

const EMPTY_CREATE_MENU_ID = 'wine-empty-create-menu';

function EmptyData({
  title,
  icon = null,
  titleClassName = '',
  rootClassName = 'root',
  onFindFromLibrary = null,
  onImportWineList = null,
  onCreateWine = null,
}) {
  const theme = useTheme();
  const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState(null);

  const handleOpenCreateMenu = useCallback(event => {
    setCreateMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseCreateMenu = useCallback(() => {
    setCreateMenuAnchorEl(null);
  }, []);

  const handlePartnerFind = useCallback(() => {
    handleCloseCreateMenu();
    onFindFromLibrary?.();
  }, [handleCloseCreateMenu, onFindFromLibrary]);

  const handlePartnerImport = useCallback(() => {
    handleCloseCreateMenu();
    onImportWineList?.();
  }, [handleCloseCreateMenu, onImportWineList]);

  const handlePartnerCreate = useCallback(() => {
    handleCloseCreateMenu();
    onCreateWine?.();
  }, [handleCloseCreateMenu, onCreateWine]);

  return (
    <Box
      sx={{
        ...classes[rootClassName],
        ...(!!titleClassName && rootClassName !== 'createOrderRoot' && classes.backgroundWhite),
      }}>
      <Box sx={classes.iconHeader}>
        {icon || <InboxIcon sx={classes.icon} />}
        <Box sx={classes.container}>
          <Box>{M.get(`emptyPage.${title}Empty`)}</Box>
        </Box>
        <Box sx={classes.footer}>
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <Button
              onClick={handleOpenCreateMenu}
              startIcon={<AddIcon sx={winesPageStyles.primaryIcon} />}
              endIcon={
                <KeyboardArrowDownIcon
                  sx={{
                    ...winesPageStyles.primaryIcon,
                    transform: createMenuAnchorEl ? 'rotate(180deg)' : 'none',
                    transition: theme.transitions.create('transform', {
                      duration: theme.transitions.duration.shorter,
                    }),
                  }}
                />
              }
              sx={winesPageStyles.primaryIconButton}
              variant="contained"
              color="primary"
              aria-haspopup="true"
              aria-controls={createMenuAnchorEl ? EMPTY_CREATE_MENU_ID : undefined}
              aria-expanded={createMenuAnchorEl ? 'true' : undefined}>
              {M.get('actions.addCreateWine')}
            </Button>
            <Popper
              id={EMPTY_CREATE_MENU_ID}
              anchorEl={createMenuAnchorEl}
              open={Boolean(createMenuAnchorEl)}
              placement="bottom-end"
              disablePortal
              modifiers={[
                { name: 'flip', enabled: false },
                { name: 'preventOverflow', enabled: false },
              ]}
              style={{ zIndex: 1300 }}>
              <ClickAwayListener onClickAway={handleCloseCreateMenu}>
                <Paper elevation={4} sx={winesPageStyles.createMenuPaper}>
                  <MenuList>
                    <MenuItem onClick={handlePartnerFind} sx={{ minHeight: 36 }}>
                      <Icon baseClassName="material-symbols-outlined" sx={winesPageStyles.createMenuListItemIcon}>
                        wine_bar
                      </Icon>
                      <Typography sx={winesPageStyles.createMenuListItemText}>
                        {M.get('actions.findWineFromLibrary')}
                      </Typography>
                    </MenuItem>
                    <MenuItem onClick={handlePartnerImport} sx={{ minHeight: 36 }}>
                      <Icon baseClassName="material-symbols-outlined" sx={winesPageStyles.createMenuListItemIcon}>
                        upload_file
                      </Icon>
                      <Typography sx={winesPageStyles.createMenuListItemText}>
                        {M.get('actions.importWineList')}
                      </Typography>
                    </MenuItem>
                    <MenuItem onClick={handlePartnerCreate} sx={{ minHeight: 36 }}>
                      <Icon baseClassName="material-symbols-outlined" sx={winesPageStyles.createMenuListItemIcon}>
                        edit
                      </Icon>
                      <Typography sx={winesPageStyles.createMenuListItemText}>
                        {M.get('actions.createNewWine')}
                      </Typography>
                    </MenuItem>
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

EmptyData.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  titleClassName: PropTypes.string,
  rootClassName: PropTypes.string,
  onFindFromLibrary: PropTypes.func,
  onImportWineList: PropTypes.func,
  onCreateWine: PropTypes.func,
};

export default EmptyData;
